// src/utils/measurement.js
//
// Client entry for the canonical measurement pipeline (see
// docs/measurement-standard.md §3.5 / §8). Soft events (impression, open,
// cta_click, share, reaction) go through the `logEvent` callable, which stamps
// `ts`, resolves identity and writes the deterministic event doc. Everything
// here is FIRE-AND-FORGET — measurement must never block or fail a user action.
//
// Attribution is capsule-anchored: a FORWARDED featured capsule is only a
// delivery envelope, so every event collapses to the ORIGINAL business capsule
// (`campaignId = originalCapsuleId`, `source = share_link`) and credits the
// forwarder via the referral chain. A direct capsule attributes to itself
// (`source = map`). Mirrors the server resolveOriginalCapsuleId().

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { getStoredDeviceIdSync } from "./deviceId";
import { isFeaturedForwardCapsule, normalizeCapsuleType } from "./timeCapsuleForward";

const IMPRESSION_THROTTLE_PREFIX = "mImpr:"; // localStorage day-grain throttle key

function normStr(value) {
  return value === undefined || value === null ? "" : String(value).trim();
}

/** UTC day, YYYY-MM-DD — matches the server dedup grain. */
function utcDay() {
  return new Date().toISOString().slice(0, 10);
}

/** Only featured capsules (sponsored / member-exclusive, or a forward of one) anchor a campaign. */
export function isMeasurableCapsule(capsule = {}) {
  const type = normalizeCapsuleType(capsule.capsuleType || capsule.type);
  return type === "sponsored" || type === "memberexclusive" || isFeaturedForwardCapsule(capsule);
}

/**
 * Resolve the canonical campaign envelope for a capsule. `token` is the share
 * token that granted access (forward/preview context), stamped into meta.
 */
export function resolveCapsuleCampaign(capsule = {}, { token = "" } = {}) {
  const capsuleId = normStr(capsule.id || capsule.capsuleId || capsule.docId);
  const isForward = isFeaturedForwardCapsule(capsule);
  const originalId = normStr(
    capsule.sourceCapsuleId || capsule.forwardSourceId || capsule.forwardedFromId,
  );
  const campaignId = isForward && originalId ? originalId : capsuleId;

  const base = {
    campaignType: "time_capsule",
    campaignId,
    source: isForward ? "share_link" : "map",
  };
  // For a forward envelope, senderId is the FORWARDER (not the business), so never fall back to it
  // — that would attribute the rollup to a user id. Omit businessId on forwards; the server rollup
  // keeps the correct businessId from the original capsule's claim/direct events (sticky merge).
  const businessId = isForward
    ? normStr(capsule.businessId)
    : normStr(capsule.businessId || capsule.senderId);
  if (businessId) base.businessId = businessId;

  const meta = {};
  if (isForward) {
    meta.referralSenderId = normStr(capsule.senderId) || null; // the forwarder
    meta.referralCampaignId = campaignId;
  }
  const tk = normStr(token || capsule.sourceShareToken);
  if (tk) meta.token = tk;
  if (Object.keys(meta).length) base.meta = meta;

  return base;
}

// Resolves true when the event reached the callable, false on any failure, so
// the impression throttle can distinguish a landed send from a dropped one and
// avoid permanently suppressing a campaign after a transient (e.g. onload)
// failure. Never rejects — measurement must not throw into a user gesture.
function callLogEvent(payload) {
  try {
    const deviceId = getStoredDeviceIdSync() || undefined;
    return Promise.resolve(
      httpsCallable(functions, "logEvent")({ ...payload, deviceId }),
    )
      .then(() => true)
      .catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

/**
 * Log a soft capsule event. `extra` may carry `token`, `source` (override),
 * `shareId` (share dedup), and `meta` (e.g. `{ reactionType: "love" }`).
 */
export function logCapsuleEvent(eventType, capsule, extra = {}) {
  const campaign = resolveCapsuleCampaign(capsule, { token: extra.token });
  if (!campaign.campaignId) return Promise.resolve(false);

  const payload = { ...campaign, eventType };
  if (extra.source) payload.source = extra.source;
  if (extra.shareId) payload.shareId = extra.shareId;
  const meta = { ...(campaign.meta || {}), ...(extra.meta || {}) };
  if (Object.keys(meta).length) payload.meta = meta;

  return callLogEvent(payload);
}

/**
 * Impression with a client-side per-campaign/day throttle (the server dedups as
 * the backstop). Silent no-op for non-measurable capsules.
 */
export function logCapsuleImpression(capsule, extra = {}) {
  if (!isMeasurableCapsule(capsule)) return;
  const campaign = resolveCapsuleCampaign(capsule);
  if (!campaign.campaignId) return;

  const key = `${IMPRESSION_THROTTLE_PREFIX}${campaign.campaignId}:${utcDay()}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1"); // optimistic — suppresses a same-render burst
  } catch {
    // storage blocked (private mode) — fall through; the server still dedups.
  }
  // Release the throttle if the send never landed (e.g. a transient onload
  // failure before functions/auth are ready), so a later surface — the gems
  // drawer, or the next marker re-render — can retry. The server dedups the
  // deterministic event id, so a retry can never double-count.
  logCapsuleEvent("impression", capsule, extra).then((sent) => {
    if (!sent) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
  });
}

/**
 * Follow / unfollow of the business behind a FEATURED capsule, attributed to the
 * capsule's campaign (§ follow scope: capsule-context surfaces only — business
 * profile follows are not campaign-attributed). No-op for non-featured capsules.
 * `isFollowing` true = follow, false = unfollow.
 */
export function logCapsuleFollow(capsule, isFollowing, extra = {}) {
  if (!isMeasurableCapsule(capsule)) return Promise.resolve();
  return logCapsuleEvent(isFollowing ? "follow" : "unfollow", capsule, extra);
}

// ── Studio raffle/coupon offers (no backing capsule) ─────────────────────────
// A nearby raffle/coupon campaign surfaced in the drawer that has no time
// capsule anchoring it (studioDb `raffleLocationIndex`). It still measures
// directly on the raffle/coupon campaign — there is simply no capsule referral.

function normalizeOfferCampaignType(value = "") {
  return String(value || "").trim().toLowerCase() === "coupon" ? "coupon" : "raffle";
}

/** Campaign envelope for a studio raffle/coupon offer (§4.1: campaignId = the raffle/coupon doc id). */
export function resolveOfferCampaign(offer = {}) {
  const base = {
    campaignType: normalizeOfferCampaignType(offer.campaignType),
    campaignId: normStr(offer.campaignId || offer.id),
  };
  const businessId = normStr(offer.businessId);
  if (businessId) base.businessId = businessId;
  return base;
}

/** Soft event (impression/open/…) for a studio offer. `extra` carries `source` (default map) and `meta`. */
export function logOfferEvent(eventType, offer = {}, extra = {}) {
  const campaign = resolveOfferCampaign(offer);
  if (!campaign.campaignId) return Promise.resolve(false);

  const payload = { ...campaign, eventType, source: extra.source || "map" };
  const meta = { ...(extra.meta || {}) };
  if (Object.keys(meta).length) payload.meta = meta;
  return callLogEvent(payload);
}

/** Offer impression with the same client-side per-campaign/day throttle as capsules. */
export function logOfferImpression(offer = {}, extra = {}) {
  const campaign = resolveOfferCampaign(offer);
  if (!campaign.campaignId) return;

  const key = `${IMPRESSION_THROTTLE_PREFIX}${campaign.campaignId}:${utcDay()}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1"); // optimistic — suppresses a same-render burst
  } catch {
    // storage blocked (private mode) — fall through; the server still dedups.
  }
  // Release the throttle on a dropped send so the impression can retry (server
  // dedups the deterministic event id, so a retry can never double-count).
  logOfferEvent("impression", offer, extra).then((sent) => {
    if (!sent) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
  });
}

/**
 * Attribute a brand-new signup to the capsule that drove it (direct journey, §3.5).
 * FIRE-AND-FORGET: the server resolves the driving campaign from this device's recent
 * open/impression events and emits the canonical `signup` event. Call once at account
 * creation; no-op without a device id and never throws into the signup flow.
 */
export function attributeSignupToCapsule() {
  try {
    const deviceId = getStoredDeviceIdSync();
    if (!deviceId) return Promise.resolve();
    return Promise.resolve(
      httpsCallable(functions, "attributeSignupToCapsule")({ deviceId }),
    ).catch(() => {});
  } catch {
    return Promise.resolve(); // measurement must never break signup
  }
}

/** A fresh per-instance share id (share dedup is per share instance, §6). */
export function newShareId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
